/*
 * Copyright (c) Nexign, JSC, 2024.
 */
/**
 *
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.30.0
 */

import { createElement } from 'react';

import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { HttpStatus } from '@/main/modules/app/appConstants';
import { makeSelectBreadCrumbsData } from '@/main/modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@/main/modules/breadCrumbs/utils/breadCrumbsGoBack';
import {
    makeSelectTranslationCommon,
    makeSelectWizardTranslation,
    makeSelectTranslationWizardCollationReport,
} from '@/main/modules/internationalization/intSelectors';
import { ICommonTranslation } from '@/main/modules/internationalization/types';
import { IWizardTranslation } from '@/main/modules/internationalization/types/wizards/wizard';
import ModalErrorCommon from '@/main/modules/modal/components/ModalError';
import { ModalMultipleOrders } from '@/main/modules/modal/components/ModalMultipleOrders';
import { modalOpen } from '@/main/modules/modal/modalActions';
import { IModalRenderProps } from '@/main/modules/modal/types/modalTypes';
import { EWizardReportParam } from '@/main/modules/wizard/components/WizardReport/WizardReportConstants';
import {
    wizardReportStorageRestoreSaga,
    wizardReportValidateSaga,
} from '@/main/modules/wizard/components/WizardReport/wizardReportSagas';
import {
    makeSelectWizardReportOrderParams,
    makeSelectWizardReportParam,
} from '@/main/modules/wizard/components/WizardReport/wizardReportSelectors';
import {
    IWizardReportOrderParams,
    IWizardReportParam,
} from '@/main/modules/wizard/components/WizardReport/wizardReportTypes';
import { wizardNextAction, wizardStorageSaveStepAction } from '@/main/modules/wizard/wizardActions';
import {
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_ACTION,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_REPORT_SET_PARAM,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_FAILED,
} from '@/main/modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '@/main/modules/wizard/wizardSagas';
import { makeSelectWizardBasketSnapshotId, makeSelectWizardStepName } from '@/main/modules/wizard/wizardSelectors';
import {
    TWizardMetaSuccessAction,
    TWizardNextSuccessAction,
    TWizardStepNames,
} from '@/main/modules/wizard/wizardTypes';
import { removeEmptyValuesFromObject } from '@/main/utils/removeEmptyValues';

import { EFormatData } from '../PageDashboard/types/widgetTypes';
import { TWizardCollationReportSetParamsPayload } from './types/wizardCollationReportActionTypes';
import {
    wizardCollationReportSetParamsAction,
    wizardCollationReportValidateFinishAction,
} from './wizardCollationReportActions';
import {
    EWizardCollationReportSteps,
    WIZARD_COLLATION_REPORT_HANDLE_NEXT,
    WIZARD_COLLATION_REPORT_SET_PARAMS,
    WIZARD_COLLATION_REPORT_VALIDATE_START,
} from './wizardCollationReportConstant';
import {
    makeSelectWizardCollationReportParam,
    makeSelectWizardCollationReportParams,
} from './wizardCollationReportSelectors';

/*
 * -------------------------------------------
 *                    INIT
 * -------------------------------------------
 */
export function* wizardCollationReportStepInitSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const stepName: string = yield select(makeSelectWizardStepName());
    const { step: wizardMetaStep } = action.payload;

    yield call(wizardCollationReportStorageRestoreSaga);

    switch (stepName) {
        case EWizardCollationReportSteps.ChooseStep:
            yield put(
                wizardCollationReportSetParamsAction({
                    formatsAvailable: wizardMetaStep.allowedFormats ?? [],
                }),
            );

            const reportFormat = yield select(makeSelectWizardCollationReportParam('reportFormat'));

            if (wizardMetaStep.allowedFormats?.length && !reportFormat) {
                yield put(wizardCollationReportSetParamsAction({ reportFormat: wizardMetaStep.allowedFormats[0] }));
            }

            break;
        case EWizardCollationReportSteps.ConfirmStep:
            yield call(wizardReportStorageRestoreSaga, action);
            break;
    }
}

/*
 * -------------------------------------------
 *                 VALIDATE
 * -------------------------------------------
 */

export function* wizardCollationReportValidateSaga(): SagaIterator {
    let isStepValid = true;
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardCollationReportSteps.ChooseStep:
            break;
        case EWizardCollationReportSteps.ConfirmStep:
            isStepValid = yield call(wizardReportValidateSaga);
            break;
    }

    yield put(wizardCollationReportValidateFinishAction({ isStepValid }));
}

/*
 * -------------------------------------------
 *                  STEP
 * -------------------------------------------
 */

export function* wizardCollationReportHandleNextSaga(): SagaIterator {
    const stepName: TWizardStepNames = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardCollationReportSteps.ChooseStep:
            yield put(
                wizardNextAction({
                    data: {},
                }),
            );
            break;
        case EWizardCollationReportSteps.ConfirmStep:
            const collationReportParams = yield select(makeSelectWizardCollationReportParams());

            const reportParams: IWizardReportOrderParams = yield select(makeSelectWizardReportOrderParams());

            const reportDestEmail: IWizardReportParam = yield select(
                makeSelectWizardReportParam(EWizardReportParam.EMAIL_DEST),
            );

            const data = removeEmptyValuesFromObject({
                reportFormat: collationReportParams.reportFormat,
                startDate: moment(collationReportParams.startDate).format(EFormatData.ReverseData),
                endDate: moment(collationReportParams.endDate).format(EFormatData.ReverseData),
            });

            const notifyParams = removeEmptyValuesFromObject({
                ...reportParams.notify,
                destEmail: reportDestEmail.isEnabled ? reportDestEmail.value : undefined,
            });

            const params = !isEmpty(notifyParams) ? { notify: notifyParams } : {};

            yield put(
                wizardNextAction(
                    removeEmptyValuesFromObject({
                        data,
                        ...params,
                    }),
                ),
            );
            break;
    }
}

export function* wizardCollationReportNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    if (stepName === EWizardCollationReportSteps.ConfirmStep) {
        const orderId = action.payload.currentStep?.step?.order?.orderId ?? '';

        const { orderCreationModal }: ICommonTranslation = yield select(makeSelectTranslationCommon());

        const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
        const goBack = () => breadCrumbGoBack(breadCrumbsData);

        yield put(
            modalOpen({
                render: () =>
                    createElement(ModalMultipleOrders, {
                        isOpen: true,
                        orderOrOrders: orderId,
                        onClose: goBack,
                        onSuccessButtonClick: breadCrumbGoBack,
                        orderTypeTranlation: orderCreationModal.success.orderType.COLLATION_REPORT,
                    }),
            }),
        );
    }
}

/*
 * -------------------------------------------
 *          SAVE DATA TO STORAGE
 * -------------------------------------------
 */

export function* wizardCollationReportStorageSaveSaga(): SagaIterator {
    yield put(
        wizardStorageSaveStepAction({
            params: {
                startDate: yield select(makeSelectWizardCollationReportParam('startDate')),
                endDate: yield select(makeSelectWizardCollationReportParam('endDate')),
                reportFormat: yield select(makeSelectWizardCollationReportParam('reportFormat')),
            },
        }),
    );
}

export function* wizardCollationReportStorageRestoreSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());

    const localStorageParams = localStorageState[basketSnapshotId] ?? {};

    const keys: Partial<Array<keyof TWizardCollationReportSetParamsPayload>> = ['endDate', 'startDate', 'reportFormat'];

    const params: TWizardCollationReportSetParamsPayload = keys.reduce((acc: Record<string, string>, cur?: string) => {
        if (!!cur && localStorageParams[cur]) {
            acc[cur] = localStorageParams[cur];
        }
        return acc;
    }, {});

    yield put(wizardCollationReportSetParamsAction(params));
}

/*
 * -------------------------------------------
 *              FAILURE HANDLER
 * -------------------------------------------
 */

export function* wizardCollationReportFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        const translationCollationReport = yield select(makeSelectTranslationWizardCollationReport());
        const wizardTranslation: IWizardTranslation = yield select(makeSelectWizardTranslation());
        title =
            translationCollationReport.error.codes[errorCode] ||
            translationCommon.error.codes[errorCode] ||
            wizardTranslation.wizardModalError.default;
    }

    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    title,
                    translation: translationCommon.modal,
                }),
        }),
    );
}

export function* wizardCollationReportSagas(): SagaIterator {
    yield all([
        takeEvery(WIZARD_META_SUCCESS, wizardCollationReportStepInitSaga),

        takeEvery(
            [WIZARD_COLLATION_REPORT_SET_PARAMS, WIZARD_COLLATION_REPORT_VALIDATE_START, WIZARD_REPORT_SET_PARAM],
            wizardCollationReportValidateSaga,
        ),

        takeEvery(WIZARD_COLLATION_REPORT_HANDLE_NEXT, wizardCollationReportHandleNextSaga),

        takeEvery([WIZARD_COLLATION_REPORT_SET_PARAMS, WIZARD_NEXT_ACTION], wizardCollationReportStorageSaveSaga),

        takeEvery(WIZARD_NEXT_SUCCESS, wizardCollationReportNextSuccessSaga),
        takeEvery(WIZARD_BACK_SUCCESS, wizardCollationReportStorageRestoreSaga),

        takeEvery(
            [WIZARD_BACK_FAILED, WIZARD_NEXT_FAILED, WIZARD_RESTORE_FAILED, WIZARD_START_FAILED],
            wizardCollationReportFailedSaga,
        ),
    ]);
}
